import { decodeBase58, encodeBase58, sha256, getBytes } from 'ethers';

const RPC_URL = import.meta.env.VITE_RPC_URL || 'http://localhost:9933';

// READ-ONLY JSON-RPC. Every chain WRITE goes through the VexSpark wallet as a
// signed bundle (see src/lib/submit.js) — the node refuses unsigned bundles
// (--reject-unsigned-bundles), so the old server-side convenience writes
// (vex_createToken, vex_mintNft, …) were removed from this file on purpose.
// Do not add a write method here: build OperationJson below instead.
async function rpc(method, params) {
  const res = await fetch(RPC_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', method, params, id: Date.now() }),
  });
  const json = await res.json();
  if (json.error) throw new Error(json.error.message || 'RPC error');
  return json.result;
}

// ═══════════════════════════════════════════════════════════════════
// Encoding helpers — shape values the way VexSpark's bundle-builder
// (toSdkOperation) parses them.
// ═══════════════════════════════════════════════════════════════════

const VXS_ALIASES = new Set([
  'VXS',
  'Vx1111111111111111111111111111111111', // Address::VXS_MINT_DISPLAY (vanity)
  '0x' + '0'.repeat(64),
  '0'.repeat(64),
]);

// Vx type prefix → full-length version byte (core/types/src/address.rs AddressType).
const VX_VERSION = {
  Vx0: 0x10, Vx1: 0x11, Vx2: 0x12, Vx3: 0x13, Vx4: 0x14, Vx5: 0x15,
  Vx6: 0x16, Vx7: 0x17, Vx8: 0x18, Vx9: 0x19, VxP: 0x1a,
};
// Legacy ~34-char forms (20-byte payload) only ever existed for Vx0/Vx1.
const VX_LEGACY_VERSION = { Vx0: 0x01, Vx1: 0x02 };

const HEX64 = /^(0x)?[0-9a-fA-F]{64}$/;
const HEX40 = /^0x[0-9a-fA-F]{40}$/;

function bytesToHex(bytes) {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function base58ToBytes(str) {
  let leadingZeros = 0;
  while (leadingZeros < str.length && str[leadingZeros] === '1') leadingZeros++;
  const n = decodeBase58(str);
  let hex = n === 0n ? '' : n.toString(16);
  if (hex.length % 2) hex = '0' + hex;
  const body = hex ? getBytes('0x' + hex) : new Uint8Array(0);
  const out = new Uint8Array(leadingZeros + body.length);
  out.set(body, leadingZeros);
  return out;
}

function checksumOk(input, expected) {
  const h = getBytes(sha256(input));
  return expected.every((b, i) => b === h[i]);
}

/** Decode any Vx-prefixed address to its 32 raw bytes, verifying the checksum. */
export function vxToBytes(addr) {
  const prefix = addr.slice(0, 3);
  const data = base58ToBytes(addr.slice(3));
  if (data.length === 37 && VX_VERSION[prefix] === data[0]) {
    if (!checksumOk(data.slice(0, 33), Array.from(data.slice(33)))) {
      throw new Error(`Bad checksum in address ${addr}`);
    }
    return data.slice(1, 33);
  }
  if (data.length === 25 && VX_LEGACY_VERSION[prefix] === data[0]) {
    const payload = data.slice(1, 21);
    if (!checksumOk(payload, Array.from(data.slice(21)))) {
      throw new Error(`Bad checksum in address ${addr}`);
    }
    const out = new Uint8Array(32);
    out.set(payload, 12);
    return out;
  }
  throw new Error(`Unrecognised Vx address: ${addr}`);
}

/** 32 raw bytes → full-length Vx display with the given type prefix (e.g. 'Vx1'). */
export function bytesToVx(bytes, prefix) {
  const version = VX_VERSION[prefix];
  const head = new Uint8Array(33);
  head[0] = version;
  head.set(bytes, 1);
  const check = getBytes(sha256(head)).slice(0, 4);
  const full = new Uint8Array(37);
  full.set(head);
  full.set(check, 33);
  return prefix + encodeBase58(full);
}

/**
 * Normalise an address for the wallet. VexSpark's parseAddress accepts Vx0/Vx1
 * and 0x (20- or 32-byte hex) ONLY — so collection (Vx2), multi-token (Vx4) and
 * other typed addresses are converted, losslessly, to 32-byte 0x hex here.
 */
export function toWalletAddress(addr, field = 'address') {
  const a = String(addr ?? '').trim();
  if (!a) throw new Error(`${field} is required`);
  if (HEX64.test(a)) return '0x' + a.replace(/^0x/, '').toLowerCase();
  if (HEX40.test(a)) return a.toLowerCase();
  if (a.startsWith('Vx') && VX_VERSION[a.slice(0, 3)] !== undefined) {
    let bytes;
    try {
      bytes = vxToBytes(a); // validates type prefix + checksum
    } catch (err) {
      throw new Error(`${field}: invalid address "${a}" (${err.message})`);
    }
    if (a.startsWith('Vx0') || a.startsWith('Vx1')) return a;
    return '0x' + bytesToHex(bytes);
  }
  throw new Error(`${field}: unsupported address "${a}" (use a Vx… or 0x… address)`);
}

/** Any accepted address form → its 32 raw bytes. */
export function addressToBytes(addr, field = 'address') {
  const w = toWalletAddress(addr, field);
  if (w.startsWith('Vx')) return vxToBytes(w);
  return getBytes('0x' + w.slice(2).padStart(64, '0'));
}

/** Token field: 'VXS' for the native token, otherwise a wallet-parsable address. */
export function toWalletToken(token, field = 'token') {
  const t = String(token ?? '').trim();
  if (!t || VXS_ALIASES.has(t) || t.toUpperCase() === 'VXS') return 'VXS';
  return toWalletAddress(t, field);
}

/** Like toWalletToken, but resolves a ticker symbol (e.g. "USDC") via vex_getTokenInfo. */
export async function resolveToken(token, field = 'token') {
  try {
    return toWalletToken(token, field);
  } catch (err) {
    const sym = String(token ?? '').trim();
    if (!/^[A-Za-z0-9]{1,10}$/.test(sym)) throw err;
    const info = await getTokenInfo(sym).catch(() => null);
    const mint = info?.mint_address_hex || info?.mint_address;
    if (!mint) throw new Error(`${field}: unknown token "${sym}"`);
    return toWalletToken(mint, field);
  }
}

/** A raw base-unit (or count / id / timestamp) integer as a decimal string. */
export function rawInt(value, field = 'amount') {
  const v = String(value ?? '').trim();
  if (!/^\d+$/.test(v)) {
    throw new Error(`${field} must be a whole number (raw base units), got "${value}"`);
  }
  return v.replace(/^0+(?=\d)/, '');
}

/**
 * A DISPLAY amount ("1.5") → raw base units ("1500000000" at 9 decimals).
 * String arithmetic only — never a JS float.
 */
export function displayToRaw(value, decimals = 9, field = 'amount') {
  const v = String(value ?? '').trim();
  const m = /^(\d*)(?:\.(\d*))?$/.exec(v);
  if (!v || !m || (m[1] === '' && !m[2])) throw new Error(`${field} must be a number, got "${value}"`);
  const frac = m[2] || '';
  if (frac.length > decimals) throw new Error(`${field} has more than ${decimals} decimal places`);
  const raw = (BigInt(m[1] || '0') * 10n ** BigInt(decimals) + BigInt((frac + '0'.repeat(decimals)).slice(0, decimals) || '0')).toString();
  return raw;
}

function optStr(v) {
  const s = v == null ? '' : String(v).trim();
  return s ? s : null;
}

/** A 32-byte hash as 0x hex. Accepts Vxh… (native display) or 0x/64-hex. */
export function toHash32Hex(value, field = 'hash') {
  const v = String(value ?? '').trim();
  if (HEX64.test(v)) return '0x' + v.replace(/^0x/, '').toLowerCase();
  if (v.startsWith('Vxh')) {
    // Same rule as Hash::from_display (primitives.rs): base58 must decode to exactly 32 bytes.
    let bytes = null;
    try { bytes = base58ToBytes(v.slice(3)); } catch { /* invalid base58 */ }
    if (bytes && bytes.length === 32) return '0x' + bytesToHex(bytes);
  }
  throw new Error(`${field} must be a 32-byte hash (Vxh… or 0x + 64 hex chars)`);
}

/** Arbitrary bytes for an anchor key: 0x-hex as given, otherwise the UTF-8 text. */
export function anchorKeyHex(key) {
  const k = String(key ?? '');
  if (/^0x([0-9a-fA-F]{2})+$/.test(k)) return k.toLowerCase();
  if (!k) throw new Error('Anchor key is required');
  return '0x' + bytesToHex(new TextEncoder().encode(k));
}

function utf8Hex(text) {
  return '0x' + bytesToHex(new TextEncoder().encode(text));
}

// ═══════════════════════════════════════════════════════════════════
// READS
// ═══════════════════════════════════════════════════════════════════

// --- VSC-7: Fungible Tokens ---
export const getTokenInfo = (mintAddress) =>
  rpc('vex_getTokenInfo', [mintAddress]);

export const listTokens = (limit = 50) =>
  rpc('vex_listTokens', [limit]);

export const getBalance = (address, token) =>
  rpc('vex_getBalance', [address, token]);

// --- VSC-21: NFTs ---
export const getNft = (collection, tokenId) =>
  rpc('vex_getNft', [collection, tokenId]);

export const listNftsByOwner = (owner, collection, limit) =>
  rpc('vex_listNftsByOwner', [owner, collection, limit]);

export const getNftCollection = (collection) =>
  rpc('vex_getNftCollection', [collection]);

// --- VSC-55: Multi-Token ---
export const getMultiTokenBalance = (collection, tokenTypeId, owner) =>
  rpc('vex_getMultiTokenBalance', [collection, tokenTypeId, owner]);

export const getTokenType = (collection, tokenTypeId) =>
  rpc('vex_getTokenType', [collection, tokenTypeId]);

export const getMultiTokenCollection = (collection) =>
  rpc('vex_getMultiTokenCollection', [collection]);

export const listTokenTypes = (collection, limit) =>
  rpc('vex_listTokenTypes', [collection, limit]);

// --- VSC-LAUNCH: Presales ---
export const getPresaleInfo = (presaleMint) =>
  rpc('vex_getPresaleInfo', [presaleMint]);

export const listPresales = (limit, status) =>
  rpc('vex_listPresales', [limit, status]);

// --- VSC-REP: Reputation ---
export const getTokenReputation = (mintAddress) =>
  rpc('vex_getTokenReputation', [mintAddress]);

// --- VexBridge ---
export const getBridgeStatus = (sourceChain, txHash) =>
  rpc('vex_getBridgeStatus', [sourceChain, txHash]);

export const getBridgeHistory = (address, limit) =>
  rpc('vex_getBridgeHistory', [address, limit]);

export const getBridgeFees = (chain) =>
  rpc('vex_getBridgeFees', [chain]);

// --- VexiDEX: Pools ---
export const quoteSwap = (fromToken, toToken, amountIn) =>
  rpc('vex_quoteSwap', [fromToken, toToken, amountIn]);

export const getPool = (tokenA, tokenB) =>
  rpc('vex_getPool', [tokenA, tokenB]);

export const listPools = (limit = 50) =>
  rpc('vex_listPools', [limit]);

// --- Network / intents ---
export const networkStatus = () =>
  rpc('vex_networkStatus', []);

/** Read-only: free text → { goal, constraints } (structured, signable). */
export const parseIntent = (text) =>
  rpc('vex_parseIntent', [text]);

export const getIntentStatus = (intentHash) =>
  rpc('vex_getIntentStatus', [intentHash]);

export const getExecutionPlan = (intent) =>
  rpc('vex_getExecutionPlan', [intent]);

// --- VIP (Vextaris Intent Programs) ---
export const getProgram = (namespace, name, version = null) =>
  rpc('vex_getProgram', [namespace, name, version]);

export const listPrograms = (namespace = null, author = null, limit = 50) =>
  rpc('vex_listPrograms', [namespace, author, limit]);

export const simulateProgram = (namespace, name, paramsJson) =>
  rpc('vex_simulateProgram', [namespace, name, paramsJson]);

export const getProgramSafety = (namespace, name) =>
  rpc('vex_getProgramSafety', [namespace, name]);

// --- Anchor (Universal Data Attestation) ---
export const getAnchors = (namespace, keyHex) =>
  rpc('vex_getAnchors', [namespace, keyHex]);

export const verifyAnchor = (namespace, keyHex, expectedHash) =>
  rpc('vex_verifyAnchor', [namespace, keyHex, expectedHash]);

// ═══════════════════════════════════════════════════════════════════
// WRITES — pure OperationJson builders (VexSpark adapter-protocol.ts).
// Each returns an OperationJson[]; submit it with signAndSend() from
// src/lib/submit.js. Nothing here talks to the node.
// ═══════════════════════════════════════════════════════════════════

// --- VSC-7 ---

/**
 * CreateToken. Returns { ops, mintAddress }.
 *
 * The chain does NOT derive a token's mint address — the operation carries it
 * (machine.rs Operation::CreateToken registers `mint_address` as given). The
 * old vex_createToken derived it server-side as
 * Address::from_contract_id(owner ‖ name ‖ symbol ‖ nonce_le) =
 * [0x00×11][0x02][SHA256(..)[0..20]] (vex_impl.rs create_token); we do the same
 * here with 8 random bytes in place of the nonce (only uniqueness matters).
 *
 * The derived MINT goes in `mintAddress` (and, for older VexSpark builds, `owner`,
 * which they mapped onto the SDK op's `mintAddress`). The creator is the bundle
 * signer; VexSpark writes the token metadata (incl. `creator_hex`, which the chain
 * checks on later edits) from the signing key.
 *
 * `totalSupply` is in WHOLE tokens: the chain multiplies initial_supply by
 * 10^decimals at execution (machine.rs Operation::CreateToken), exactly as the
 * old vex_createToken(initial_supply) did.
 */
export function createTokenOps({ name, symbol, decimals, wholeSupply, creator }) {
  const creatorBytes = addressToBytes(creator, 'creator');
  const nonce = crypto.getRandomValues(new Uint8Array(8));
  const enc = new TextEncoder();
  const nameB = enc.encode(name);
  const symB = enc.encode(symbol);
  const uid = new Uint8Array(32 + nameB.length + symB.length + 8);
  uid.set(creatorBytes, 0);
  uid.set(nameB, 32);
  uid.set(symB, 32 + nameB.length);
  uid.set(nonce, 32 + nameB.length + symB.length);
  const digest = getBytes(sha256(uid));
  const mint = new Uint8Array(32);
  mint[11] = 0x02;
  mint.set(digest.slice(0, 20), 12);
  const mintHex = '0x' + bytesToHex(mint);
  const dec = Number.parseInt(decimals, 10);
  if (!Number.isInteger(dec) || dec < 0 || dec > 18) throw new Error('Decimals must be 0-18');
  return {
    mintAddress: bytesToVx(mint, 'Vx1'),
    mintAddressHex: mintHex,
    ops: [{
      type: 'CreateToken',
      name: String(name),
      symbol: String(symbol),
      decimals: dec,
      totalSupply: rawInt(wholeSupply, 'Initial supply'),
      mintAddress: mintHex, // VexSpark ≥ 2026-10-31 reads this and writes the token metadata itself
      owner: mintHex,       // older VexSpark builds read `owner` as the mint — kept so both work
    }],
  };
}

/**
 * Pre-flight the symbol-uniqueness check the old vex_createToken did server-side
 * (execution does not re-check it). Best effort: a read failure does not block.
 */
export async function assertSymbolAvailable(symbol) {
  const info = await getTokenInfo(String(symbol)).catch(() => null);
  if (info && String(info.symbol || '').toUpperCase() === String(symbol).toUpperCase()) {
    throw new Error(`Symbol '${symbol}' already taken`);
  }
}

export const transferOps = (to, token, amount) => [{
  type: 'Transfer',
  to: toWalletAddress(to, 'recipient'),
  token: toWalletToken(token),
  amount: rawInt(amount),
}];

export const updateTokenMetadataOps = (mint, f = {}) => [{
  type: 'UpdateTokenMetadata',
  mint: toWalletAddress(mint, 'mint'),
  metadataUri: optStr(f.metadataUri),
  image: optStr(f.image),
  description: optStr(f.description),
  externalUrl: optStr(f.externalUrl),
  twitter: optStr(f.twitter),
  discord: optStr(f.discord),
  telegram: optStr(f.telegram),
  github: optStr(f.github),
}];

export const lockTokenMetadataOps = (mint) => [{
  type: 'LockTokenMetadata',
  mint: toWalletAddress(mint, 'mint'),
}];

// --- VSC-21: NFTs ---
// The collection address is derived BY THE CHAIN at execution
// (blake3(user:name:symbol:block_timestamp), machine.rs CreateNftCollection) — it
// cannot be known before inclusion. Callers show the tx hash instead.
export const createNftCollectionOps = ({ name, symbol, baseUri, maxSupply, royaltyBps, owner, description, iconUri, bannerUri, externalUrl }) => [{
  type: 'CreateNftCollection',
  name: String(name),
  symbol: String(symbol),
  baseUri: String(baseUri ?? ''),
  maxSupply: maxSupply == null || String(maxSupply).trim() === '' || String(maxSupply).trim() === '0'
    ? null : rawInt(maxSupply, 'Max supply'),
  royaltyBps: Number.parseInt(royaltyBps || '0', 10) || 0,
  description: String(description ?? ''),
  iconUri: optStr(iconUri),
  bannerUri: optStr(bannerUri),
  externalUrl: optStr(externalUrl),
  owner: toWalletAddress(owner, 'owner'),
}];

export const mintNftOps = (collection, tokenId, metadataUri, recipient) => [{
  type: 'MintNft',
  collection: toWalletAddress(collection, 'collection'),
  tokenId: rawInt(tokenId, 'Token ID'),
  metadataUri: String(metadataUri ?? ''),
  recipient: toWalletAddress(recipient, 'recipient'),
}];

export const transferNftOps = (collection, tokenId, to) => [{
  type: 'TransferNft',
  collection: toWalletAddress(collection, 'collection'),
  tokenId: rawInt(tokenId, 'Token ID'),
  to: toWalletAddress(to, 'recipient'),
}];

export const burnNftOps = (collection, tokenId) => [{
  type: 'BurnNft',
  collection: toWalletAddress(collection, 'collection'),
  tokenId: rawInt(tokenId, 'Token ID'),
}];

// --- VSC-55: Multi-Token ---
// Collection address is chain-derived at execution, as for NFTs.
export const createMultiTokenCollectionOps = ({ name, symbol, baseUri, royaltyBps, owner, description, iconUri, bannerUri, externalUrl }) => [{
  type: 'CreateMultiTokenCollection',
  name: String(name),
  symbol: String(symbol),
  baseUri: String(baseUri ?? ''),
  royaltyBps: Number.parseInt(royaltyBps || '0', 10) || 0,
  description: String(description ?? ''),
  iconUri: optStr(iconUri),
  bannerUri: optStr(bannerUri),
  externalUrl: optStr(externalUrl),
  owner: toWalletAddress(owner, 'owner'),
}];

export const defineTokenTypeOps = (collection, tokenTypeId, name, metadataUri, maxSupply, isFungible) => [{
  type: 'DefineTokenType',
  collection: toWalletAddress(collection, 'collection'),
  tokenTypeId: rawInt(tokenTypeId, 'Token type ID'),
  name: String(name),
  metadataUri: String(metadataUri ?? ''),
  maxSupply: maxSupply == null || String(maxSupply).trim() === '' ? null : rawInt(maxSupply, 'Max supply'),
  isFungible: !!isFungible,
}];

export const mintMultiTokenOps = (collection, tokenTypeId, to, amount) => [{
  type: 'MintMultiToken',
  collection: toWalletAddress(collection, 'collection'),
  tokenTypeId: rawInt(tokenTypeId, 'Token type ID'),
  to: toWalletAddress(to, 'recipient'),
  amount: rawInt(amount),
}];

export const batchMintMultiTokenOps = (collection, mints) => [{
  type: 'BatchMintMultiToken',
  collection: toWalletAddress(collection, 'collection'),
  mints: mints.map((m) => ({
    tokenTypeId: rawInt(m.tokenTypeId, 'Token type ID'),
    recipient: toWalletAddress(m.recipient, 'recipient'),
    amount: rawInt(m.amount),
  })),
}];

export const transferMultiTokenOps = (collection, tokenTypeId, to, amount) => [{
  type: 'TransferMultiToken',
  collection: toWalletAddress(collection, 'collection'),
  tokenTypeId: rawInt(tokenTypeId, 'Token type ID'),
  to: toWalletAddress(to, 'recipient'),
  amount: rawInt(amount),
}];

export const batchTransferMultiTokenOps = (collection, transfers) => [{
  type: 'BatchTransferMultiToken',
  collection: toWalletAddress(collection, 'collection'),
  transfers: transfers.map((t) => ({
    tokenTypeId: rawInt(t.tokenTypeId, 'Token type ID'),
    recipient: toWalletAddress(t.recipient, 'recipient'),
    amount: rawInt(t.amount),
  })),
}];

export const burnMultiTokenOps = (collection, tokenTypeId, amount) => [{
  type: 'BurnMultiToken',
  collection: toWalletAddress(collection, 'collection'),
  tokenTypeId: rawInt(tokenTypeId, 'Token type ID'),
  amount: rawInt(amount),
}];

// --- VSC-LAUNCH: Presales --- (all amounts raw base units, times unix seconds)
export const createPresaleOps = (p) => {
  const lpPercent = Number.parseInt(p.lpPercent, 10);
  if (!Number.isInteger(lpPercent)) throw new Error('LP percentage must be a whole number');
  return [{
    type: 'CreatePresale',
    tokenMint: toWalletAddress(p.tokenMint, 'token mint'),
    tokenAllocation: rawInt(p.tokenAllocation, 'Token allocation'),
    pricePerToken: rawInt(p.pricePerToken, 'Price per token'),
    minContribution: rawInt(p.minContribution, 'Min contribution'),
    maxContribution: rawInt(p.maxContribution, 'Max contribution'),
    softCap: rawInt(p.softCap, 'Soft cap'),
    hardCap: rawInt(p.hardCap, 'Hard cap'),
    startTime: rawInt(p.startTime, 'Start time'),
    endTime: rawInt(p.endTime, 'End time'),
    lpPercent,
    lpLockDuration: rawInt(p.lpLockDuration, 'LP lock duration'),
  }];
};

export const contributePresaleOps = (presaleMint, amount) => [{
  type: 'ContributePresale',
  presaleMint: toWalletAddress(presaleMint, 'presale'),
  amount: rawInt(amount),
}];

const presaleAction = (type) => (presaleMint) => [{
  type,
  presaleMint: toWalletAddress(presaleMint, 'presale'),
}];
export const claimPresaleOps = presaleAction('ClaimPresale');
export const finalizePresaleOps = presaleAction('FinalizePresale');
export const cancelPresaleOps = presaleAction('CancelPresale');
export const refundPresaleOps = presaleAction('RefundPresale');

// --- VexBridge ---
// Inbound deposits (old vex_bridgeToken) are deliberately absent: they submit as
// the chain's SYSTEM account, which no browser wallet can sign, and the bridge is
// paused on-chain. Only the user-signed outbound withdrawal is offered.
export const BRIDGE_DEPOSITS_PAUSED = true;

export const bridgeWithdrawOps = (token, amount, destChain, destAddress) => [{
  type: 'BridgeWithdraw',
  token: toWalletToken(token),
  amount: rawInt(amount),
  destChain: String(destChain),
  destAddress: String(destAddress).trim(),
}];

// --- VexiDEX: Pools --- (tokens must already be resolved — see resolveToken)
export const createPoolOps = (tokenA, tokenB, amountA, amountB, lpLockDuration = 0) => [{
  type: 'CreatePool',
  tokenA: toWalletToken(tokenA, 'token A'),
  tokenB: toWalletToken(tokenB, 'token B'),
  amountA: rawInt(amountA, 'Amount A'),
  amountB: rawInt(amountB, 'Amount B'),
  lpLockDuration: Number.parseInt(lpLockDuration || '0', 10) || 0,
}];

// --- Intents ---
/**
 * Build a signed-rail Intent from vex_parseIntent's output. The wallet signs the
 * STRUCTURED goal (and shows it on the approval screen), never the free text.
 */
export const intentOps = (parsed, originalText) => {
  if (!parsed || typeof parsed !== 'object' || !parsed.goal) {
    throw new Error('Intent could not be parsed into a signable goal');
  }
  return [{
    type: 'Intent',
    goal: parsed.goal,
    constraints: parsed.constraints ?? undefined,
    originalText: originalText ? String(originalText).slice(0, 500) : null,
    sourceParser: 'native',
  }];
};

// --- VIP ---
/**
 * RegisterProgram. The old vex_registerProgram put the program JSON's UTF-8
 * bytes straight into program_data (vex_impl.rs register_program); the wallet
 * takes those bytes hex-encoded.
 */
export const registerProgramOps = (programJsonString) => [{
  type: 'RegisterProgram',
  programData: utf8Hex(programJsonString),
}];

export const flagProgramOps = (namespace, name, reason, evidenceUri = null) => [{
  type: 'FlagProgram',
  namespace: String(namespace),
  name: String(name),
  reason: String(reason),
  evidenceUri: optStr(evidenceUri),
}];

export const deactivateProgramOps = (namespace, name) => [{
  type: 'DeactivateProgram',
  namespace: String(namespace),
  name: String(name),
}];

/**
 * ExecuteProgram. Params object → ordered [key, value] string pairs, sorted by key
 * to match the old vex_executeProgram (serde_json::Map iterates in key order).
 */
export const executeProgramOps = (namespace, name, params = {}, version = null) => [{
  type: 'ExecuteProgram',
  namespace: String(namespace),
  name: String(name),
  params: Object.keys(params).sort().map((k) => {
    const v = params[k];
    return [k, typeof v === 'string' ? v : JSON.stringify(v)];
  }),
  version: version == null ? null : Number(version),
}];

// --- Anchor ---
export const anchorOps = (namespace, keyHex, valueHash, metadataUri = null) => [{
  type: 'Anchor',
  namespace: String(namespace),
  key: keyHex,
  valueHash: toHash32Hex(valueHash, 'Value hash'),
  metadataUri: optStr(metadataUri),
}];
